import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        // Check if we have tokens in localStorage
        const accessToken = authService.getAccessToken();
        const refreshToken = authService.getRefreshToken();
        const adminData = authService.getCurrentAdmin();

        console.log('🔍 Auth Init - Access Token:', accessToken ? 'EXISTS' : 'MISSING');
        console.log('🔍 Auth Init - Refresh Token:', refreshToken ? 'EXISTS' : 'MISSING');
        console.log('🔍 Auth Init - Admin Data:', adminData);

        // If we have tokens and admin data, consider user authenticated
        // The API interceptor will handle token refresh if needed
        if (accessToken && refreshToken) {
          console.log('✅ User authenticated, setting state');
          setAdmin(adminData);
          setIsAuthenticated(true);
        } else {
          // No valid session found
          console.log('❌ No valid session found, clearing auth data');
          authService.clearAuthData();
          setAdmin(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        authService.clearAuthData();
        setAdmin(null);
        setIsAuthenticated(false);
      } finally {
        console.log('🏁 Auth Init Complete - isAuthenticated:', isAuthenticated);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login Step 1 - Credential verification
  const loginStep1 = async (email, password, turnstileToken) => {
    try {
      const result = await authService.loginStep1(email, password, turnstileToken);

      if (result.success) {
        if (result.otpRequired) {
          // OTP required - return session_id for step 2
          console.log('📧 OTP required, session_id received');
          return {
            success: true,
            otpRequired: true,
            sessionId: result.data.session_id,
            message: result.data.message,
          };
        } else {
          // OTP not required - login complete
          console.log('✅ Login successful (OTP disabled), setting admin state:', result.data.admin);
          setAdmin(result.data.admin);
          setIsAuthenticated(true);
          return { success: true, otpRequired: false };
        }
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: 'An unexpected error occurred during login',
      };
    }
  };

  // Login Step 2 - OTP verification
  const loginStep2 = async (sessionId, otp) => {
    try {
      const result = await authService.loginStep2(sessionId, otp);

      if (result.success) {
        console.log('✅ OTP verification successful, setting admin state:', result.data.admin);
        setAdmin(result.data.admin);
        setIsAuthenticated(true);

        // Verify tokens were stored
        console.log('🔍 After login - Tokens in localStorage:');
        console.log('  Access Token:', localStorage.getItem('accessToken') ? 'STORED' : 'MISSING');
        console.log('  Refresh Token:', localStorage.getItem('refreshToken') ? 'STORED' : 'MISSING');
        console.log('  Admin Data:', localStorage.getItem('adminData') ? 'STORED' : 'MISSING');

        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: 'An unexpected error occurred during OTP verification',
      };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAdmin(null);
      setIsAuthenticated(false);
    }
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const result = await authService.refreshToken();
      return result.success;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  // Update admin data
  const updateAdmin = (newAdminData) => {
    const updatedAdmin = { ...admin, ...newAdminData };
    setAdmin(updatedAdmin);
    localStorage.setItem('adminData', JSON.stringify(updatedAdmin));
  };

  const value = {
    admin,
    isAuthenticated,
    loading,
    loginStep1,
    loginStep2,
    logout,
    refreshToken,
    updateAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
